import Backdrop from "@mui/material/Backdrop";
import Grow from "@mui/material/Grow";
import Modal from "@mui/material/Modal";
import makeStyles from '@mui/styles/makeStyles';
import {color, font, radius, shadow, type} from "common/tokens";
import {BadgeCheck} from "lucide-react";
import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";

// Confirms the booking and nothing else — the event is already on the list
// behind it, so there is nothing here worth reading twice. It takes itself away
// rather than asking for a dismissal.
const DISMISS_AFTER = 2600;

const useStyles = makeStyles({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    transformOrigin: 'center center',
    background: color.ACCENT_TINT,
    borderRadius: radius.MEDIA_CARD,
    boxShadow: shadow.CARD,
    boxSizing: 'border-box',
    width: 510,
    maxWidth: '100%',
    padding: '44px 40px',
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
  },
  // The badge lands first and the words follow it, so the card reads as a
  // confirmation happening rather than one that was already there.
  badge: {
    color: color.SURFACE,
    display: 'flex',
    animation: '$badgeIn 420ms cubic-bezier(0.22, 1.2, 0.36, 1) both',
  },
  title: {
    ...type.body,
    fontSize: 18,
    fontWeight: font.BOLD,
    color: color.TEXT,
    textAlign: 'center',
    margin: 0,
    animation: '$titleIn 320ms ease-out 180ms both',
  },
  // Counts the card down instead of letting it vanish mid-read.
  timer: {
    width: '100%',
    height: 3,
    borderRadius: 3,
    background: color.SURFACE,
    overflow: 'hidden',
  },
  timerFill: {
    display: 'block',
    height: '100%',
    background: color.ACCENT,
    transformOrigin: 'left center',
    animation: `$drain ${DISMISS_AFTER}ms linear both`,
  },
  '@keyframes badgeIn': {
    '0%': {opacity: 0, transform: 'scale(0.4)'},
    '100%': {opacity: 1, transform: 'scale(1)'},
  },
  '@keyframes titleIn': {
    '0%': {opacity: 0, transform: 'translateY(8px)'},
    '100%': {opacity: 1, transform: 'none'},
  },
  '@keyframes drain': {
    '0%': {transform: 'scaleX(1)'},
    '100%': {transform: 'scaleX(0)'},
  },
});

const BookingConfirmation = ({isEdit = false, onClose}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  useEffect(() => {
    const timer = setTimeout(onClose, DISMISS_AFTER);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <Modal
      open
      onClose={onClose}
      className={classes.modal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{timeout: 220}}
    >
      <Grow in timeout={{enter: 240, exit: 160}}>
        <div className={classes.card}>
          <span className={classes.badge}>
            <BadgeCheck size={100} strokeWidth={1.5} fill={color.SUCCESS}/>
          </span>
          <p className={classes.title}>
            {isEdit ? t('eventUpdatedSuccessfully') : t('eventCreatedSuccessfully')}
          </p>
          <span className={classes.timer}>
            <span className={classes.timerFill}/>
          </span>
        </div>
      </Grow>
    </Modal>
  );
};

export default BookingConfirmation;
